import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getCurrentPlan } from 'src/services/plan/current-plan.service';
import { miniSerializeErrorOverride } from './serialize-error';
import logger from 'src/logger';
import { TCurrentPlan, TPlan } from 'src/types/plan.type';
import { getSessionIdentifier } from 'src/helpers/session.helpers';

// Initial state for the plan slice
const initialState: TCurrentPlan = {
  orderDetails: {},
};

// Create the slice
const planSlice = createSlice({
  name: 'currentPlan',
  initialState,
  reducers: {
    // Reducer to update the plan state with payload
    setPlanData: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
});

export const { setPlanData } = planSlice.actions;

// Thunk to fetch the current plan and dispatch the whole response
export const getCurrentPlanAction = createAsyncThunk<void, void, { state: RootState }>(
  'currentPlan/getCurrentPlan',
  async (_, { dispatch, getState }) => {
    const { session, currentPlan } = getState() as RootState;

    if (!session) {
      throw new Error('Session not found');
    }

    // Check if data already exists in store
    if (currentPlan.orderDetails && Object.keys(currentPlan.orderDetails).length > 0) {
      // Data already exists, so do not make the API call
      return;
    }

    try {
      const response = await getCurrentPlan(getSessionIdentifier(session));

      // Dispatch the update with the full response
      dispatch(setPlanData(response));
    } catch (error) {
      logger.reportError(error, { message: 'Error in getCurrentPlanAction' });
      throw error;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

export const selectPlanData = (state: RootState): TCurrentPlan => state.currentPlan;

// Selector to check if the current plan is on beta (planType should have 'beta')
export const selectIsBeta = (state: RootState): boolean => {
  const currentPlan = selectPlanData(state);
  const site = currentPlan.orderDetails?.Site;
  return site ? site.planType.includes('beta') : false;
};

export const selectIsFreeProvisionedSite = (state: RootState): boolean => {
  const currentPlan = selectPlanData(state);
  const site = currentPlan.orderDetails?.Site;
  return site ? site.planType === 'free_site' : false;
};

// Selector to check if Mail is purchased
export const selectIsMailPurchased = (state: RootState): boolean => {
  const currentPlan = selectPlanData(state);
  return !!currentPlan?.orderDetails?.MailSuite;
};

export const selectMailPlan = (state: RootState): TPlan => {
  const currentPlan = selectPlanData(state);
  return currentPlan.orderDetails?.MailSuite;
};

export const selectSitePlan = (state: RootState): TPlan => {
  const currentPlan = selectPlanData(state);
  return currentPlan.orderDetails?.Site;
};

export const selectDomainPlan = (state: RootState): TPlan => {
  const currentPlan = selectPlanData(state);
  return currentPlan.orderDetails?.NeoDomain;
};

// Selector to check if Site is purchased
export const selectIsSitePurchased = (state: RootState): boolean => {
  const currentPlan = selectPlanData(state);
  return !!currentPlan?.orderDetails?.Site;
};

// Selector to check if the current plan includes "plus" or "growth"
export const selectIsPlusOrGrowth = (state: RootState): boolean => {
  const currentPlan = selectPlanData(state);
  const planType = currentPlan?.orderDetails?.Site?.planType;
  return planType?.includes('plus') || planType?.includes('growth');
};

export const selectPlanType = (state: RootState): string => {
  const currentPlan = selectPlanData(state);
  const planType = currentPlan?.orderDetails?.Site?.planType;
  return planType;
};

export const selectCurrentPlanOrderIds = (state: RootState) => {
  const currentPlan = selectPlanData(state);
  const mailOrder = currentPlan.orderDetails?.MailSuite;
  const siteOrder = currentPlan.orderDetails?.Site;
  const domainOrder = currentPlan.orderDetails?.NeoDomain;

  return {
    ...(mailOrder ? { mailOrderId: mailOrder.id } : {}),
    ...(siteOrder ? { siteOrderId: siteOrder.id } : {}),
    ...(domainOrder ? { domainOrderId: domainOrder.id } : {}),
  };
};

export default planSlice.reducer;
